import { createFileRoute, useParams } from '@tanstack/react-router'
import { useEffect } from 'react'
import { AssetDetailPage } from '../../../components/AssetDetailPage'
import { useAsset } from '../../../hooks/useQueries'
import { useUrlTab } from '../../../hooks/useUrlTab'

type AssetDetailSearch = {
    tab?: string
    propertyHistory?: string
    // Data tab: `show=all` reveals the empty "add value" ghost rows (default
    // hides them). See spec 2026-07-09-data-tab-hide-empty-rows.
    show?: string
    // Plan tab deep-link state — see AssetPlanViewer.
    rep?: string
    annotation?: string
    // Building Plan tab mode: `columns` shows the riser diagram instead of the
    // stacking plan (spec 2026-10-19-03 §2). See BuildingPlanTab.
    view?: string
    // Site Map tab deep-link — open a building's panel. See SiteMapTab.
    building?: string
    // Equipment tab deep-link — pre-fill and open the Add Equipment dialog on
    // this asset-type code. Consumed by EquipmentTab.tsx and set by a
    // reconciliation issue's "Complete the inventory" CTA (spec
    // 2026-07-24-04-declared-vs-observed-property-reconciliation.md).
    addEquipmentType?: string
    // Properties tab deep-link — scroll this property's row into view and
    // pulse it. Set by a document highlight box's "back to its source" click
    // (user, 2026-09-16); consumed by AssetProperties via useFocusAndPulseRow.
    focusProperty?: string
    // Decisions tab filters (spec 2026-10-10-08): search words, outcome
    // (adopted | rejected | not_voted), resolution subject, link kind.
    decisionQ?: string
    decisionOutcome?: string
    decisionSubject?: string
    decisionLink?: string
    // Activity tab deep-link (spec 2026-11-01-01): scroll this knowledge note
    // into view and highlight it. Set by a cited note on the triage card.
    note?: string
}

export const Route = createFileRoute('/$org/assets/$assetUid')({
    component: AssetDetail,
    validateSearch: (search: Record<string, unknown>): AssetDetailSearch => {
        const out: AssetDetailSearch = {}
        if (typeof search.tab === 'string') out.tab = search.tab
        if (typeof search.propertyHistory === 'string') out.propertyHistory = search.propertyHistory
        if (typeof search.show === 'string') out.show = search.show
        if (typeof search.rep === 'string') out.rep = search.rep
        if (typeof search.annotation === 'string') out.annotation = search.annotation
        if (search.view === 'columns') out.view = search.view
        if (typeof search.building === 'string') out.building = search.building
        if (typeof search.addEquipmentType === 'string') out.addEquipmentType = search.addEquipmentType
        if (typeof search.focusProperty === 'string') out.focusProperty = search.focusProperty
        if (typeof search.decisionQ === 'string' && search.decisionQ) out.decisionQ = search.decisionQ
        if (typeof search.decisionOutcome === 'string' && search.decisionOutcome) out.decisionOutcome = search.decisionOutcome
        if (typeof search.decisionSubject === 'string' && search.decisionSubject) out.decisionSubject = search.decisionSubject
        if (typeof search.decisionLink === 'string' && search.decisionLink) out.decisionLink = search.decisionLink
        if (typeof search.note === 'string' && search.note) out.note = search.note
        return out
    },
})

function AssetDetail() {
    const { org, assetUid } = useParams({ from: '/$org/assets/$assetUid' })
    const { data: assetData } = useAsset(org, assetUid)

    // Read tab from URL — when absent we let AssetDetailPage pick the default
    // tab based on the asset type (plan for plannable types, otherwise properties).
    // Legacy redirects:
    //   - `?tab=equipments` (old ghost tab) → `?tab=equipment`
    //     (spec 2026-05-02-01-equipment-tab §4)
    //   - `?tab=identity` → `?tab=properties`
    //     (spec 2026-05-06-02 — Identity tab renamed to Properties)
    // Both kept for one release cycle. Rewritten synchronously so the URL
    // reflects the canonical name before any UI paints.
    if (typeof window !== 'undefined') {
        const sp = new URLSearchParams(window.location.search)
        let changed = false
        if (sp.get('tab') === 'equipments') {
            sp.set('tab', 'equipment')
            changed = true
        }
        if (sp.get('tab') === 'identity') {
            sp.set('tab', 'properties')
            changed = true
        }
        if (changed) {
            const url = new URL(window.location.href)
            url.search = sp.toString()
            window.history.replaceState({}, '', url.toString())
        }
    }
    // Tab lives in the URL; useUrlTab re-syncs it when the asset changes and on
    // browser back/forward (issue #1017).
    const [activeTab, setActiveTab] = useUrlTab(assetUid)

    // Helper function to get asset title
    const getAssetTitle = (asset: Record<string, unknown>) => {
        if (asset.name) {
            return asset.name as string
        }
        if (asset.external_ids && typeof asset.external_ids === 'object' && asset.external_ids !== null) {
            const externalIds = asset.external_ids as Record<string, unknown>
            if (externalIds.code) {
                return externalIds.code as string
            }
        }
        return asset.uid as string
    }

    // Update document title when asset data is available
    useEffect(() => {
        if (assetData) {
            const assetTitle = getAssetTitle(assetData)
            document.title = `${assetTitle} - Buildtory`
        }

        // Cleanup: restore default title when component unmounts
        return () => {
            document.title = 'Buildtory'
        }
    }, [assetData])

    return <AssetDetailPage activeTab={activeTab} setActiveTab={setActiveTab} />
}